import React, { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon, Video, Loader2, CheckCircle } from "lucide-react";
import { API } from "../App";
import axios from "axios";

const CloudinaryUpload = ({ onUploadComplete, existingUrls = [], maxFiles = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaUrls, setMediaUrls] = useState(existingUrls);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Limit total files
    const remainingSlots = maxFiles - mediaUrls.length;
    const filesToUpload = files.slice(0, remainingSlots);

    if (filesToUpload.length === 0) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);

    for (const file of filesToUpload) {
      const fileId = `${Date.now()}_${file.name}`;
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

      try {
        // Determine resource type
        const isVideo = file.type.startsWith("video/");
        const resourceType = isVideo ? "video" : "image";

        // Get signature from backend - uses httpOnly cookie auth
        const signatureRes = await axios.get(
          `${API}/cloudinary/signature?resource_type=${resourceType}`,
          { withCredentials: true }
        );

        const { signature, timestamp, cloud_name, api_key, folder } = signatureRes.data;

        // Upload to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("signature", signature);
        formData.append("timestamp", timestamp);
        formData.append("api_key", api_key);
        formData.append("folder", folder);

        const uploadRes = await axios.post(
          `https://api.cloudinary.com/v1_1/${cloud_name}/${resourceType}/upload`,
          formData,
          {
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [fileId]: percent }));
            }
          }
        );

        const newUrl = uploadRes.data.secure_url;
        setMediaUrls(prev => {
          const updated = [...prev, newUrl];
          onUploadComplete(updated);
          return updated;
        });

        // Mark as complete
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        setTimeout(() => {
          setUploadProgress(prev => {
            const { [fileId]: _, ...rest } = prev;
            return rest;
          });
        }, 1000);

      } catch (error) {
        console.error("Upload error:", error);
        setUploadProgress(prev => {
          const { [fileId]: _, ...rest } = prev;
          return rest;
        });
        alert(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeMedia = (urlToRemove) => {
    setMediaUrls(prev => {
      const updated = prev.filter((url) => url !== urlToRemove);
      onUploadComplete(updated);
      return updated;
    });
  };

  const isVideo = (url) => {
    return url.includes("/video/") || /\.(mp4|webm|mov|avi)$/i.test(url);
  };

  return (
    <div className="space-y-4">
      {/* Media Preview Grid */}
      {mediaUrls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {mediaUrls.map((url) => (
            <div key={url} className="relative group rounded-xl overflow-hidden bg-white/5 border border-white/10 aspect-video">
              {isVideo(url) ? (
                <video
                  src={url}
                  className="w-full h-full object-cover"
                  controls={false}
                />
              ) : (
                <img
                  src={url}
                  alt="Campaign media"
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => removeMedia(url)}
                  data-testid={`remove-media-${mediaUrls.indexOf(url)}`}
                  className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="absolute bottom-2 left-2">
                {isVideo(url) ? (
                  <Video className="w-5 h-5 text-white drop-shadow-lg" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-white drop-shadow-lg" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {Object.entries(uploadProgress).map(([fileId, progress]) => (
        <div key={fileId} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
          {progress < 100 ? (
            <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-400" />
          )}
          <div className="flex-1">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <span className="text-sm text-gray-400">{progress}%</span>
        </div>
      ))}

      {/* Upload Button */}
      {mediaUrls.length < maxFiles && (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id="cloudinary-upload"
            data-testid="cloudinary-file-input"
          />
          <label
            htmlFor="cloudinary-upload"
            className={`w-full py-6 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
              uploading 
                ? "border-cyan-500/50 bg-cyan-500/5" 
                : "border-white/20 hover:border-white/40 hover:bg-white/5"
            }`}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            ) : (
              <Upload className="w-8 h-8 text-gray-400" />
            )}
            <span className="text-gray-400">
              {uploading ? "Uploading..." : "Click to upload images or videos"}
            </span>
            <span className="text-xs text-gray-500">
              {mediaUrls.length} / {maxFiles} files uploaded
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default CloudinaryUpload;
